import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useAdminPermissions} from 'src/admin-rights-service/modules/admin-profile/sections/admin-permissions';
import {useAdminRoleSelect} from 'src/admin-rights-service/modules/admin-profile/sections/admin-role-select';
import {useAccountTeamMemberSettings} from 'src/admin-rights-service/modules/roles-page';
import {useRolesPermissions} from 'src/admin-rights-service/modules/roles-page/useRolesPermissions';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {Dispatch, ReactElement, SetStateAction} from 'react';
import type {SaveOptions, SaveWithReason} from 'src/components/save-with-reason/save-with-reason-button';
import type {MessageContextType} from 'src/contexts/message-context';
import type {AccountManagerSettings} from 'src/openapi/admin/models/AccountManagerSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {ProfileRecord} from 'src/openapi/admin/models/ProfileRecord';

type UserRoleContent = {
    isLoading: boolean,
    loadingError: Error | null,
    busy: boolean,
    isChunkChanged: boolean,

    roles: ProfileRecord[],
    role: ProfileRecord | null, changeRole: (newRole: (ProfileRecord | null)) => Promise<void>,
    defaultAdminPermissions: string[],
    adminPermissions: string[],
    setAdminPermissions: Dispatch<SetStateAction<string[]>>,
    accountTeamMemberSettings: AccountManagerSettings,
    setAccountTeamMemberSettings: Dispatch<SetStateAction<AccountManagerSettings>>,

    isDefaultAdminPermissions: boolean,
    resetAdminPermissions: () => void,
    saveChanges: SaveWithReason,
    discardChanges: () => void,
};

type Props = {
    userId: number,
    children: (content: UserRoleContent) => ReactElement,
    onProceed: () => void,
};

export const UserRoleSettingsController = ({userId, children, onProceed}: Props) => {
    const [busy, setBusy] = useState(false);

    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-role-permissions',
        onProceed,
    });

    const {
        isLoading: isAccountTeamMemberSettingsLoading,
        loadingError: accountTeamMemberSettingsLoadingError,
        isChanged: isAccountTeamMemberSettingsChanged,
        data: accountTeamMemberSettings,
        setData: setAccountTeamMemberSettings,
        saveChanges: saveAccountTeamMemberSettings,
        discardChanges: discardAccountTeamMemberSettings,
    } = useAccountTeamMemberSettings({userId});

    const {
        isLoading: isRolesPermissionsLoading,
        loadingError: rolesPermissionsLoadingError,
        data: roles,
    } = useRolesPermissions();

    const {
        isLoading: isUserPermissionsLoading,
        loadingError: permissionsLoadingError,
        adminPermissions,
        isInitialAdminPermissions,
        setAdminPermissions,
        saveAdminPermissions,
        discardAdminPermissions,
    } = useAdminPermissions({userId});

    const {
        isLoading: isUserRoleLoading,
        loadingError: userRoleLoadingError,
        isRoleChanged,

        role, changeRole,
        initialRole, setInitialRole,

        defaultAdminPermissions,
        discardAdminRole,
        resetAdminPermissions,
    } = useAdminRoleSelect({userId, onPermissionsChanged: setAdminPermissions});

    useEffect(() => {
        setIsChunkChanged(!isInitialAdminPermissions || isRoleChanged || isAccountTeamMemberSettingsChanged);
    }, [isInitialAdminPermissions, isRoleChanged, isAccountTeamMemberSettingsChanged, setIsChunkChanged]);

    const saveUserRolePermissions = useCallback(async (options?: SaveOptions) => {
        const calls = [];
        setBusy(true);
        try {

            if (!isInitialAdminPermissions || isRoleChanged) {
                const error = await saveAdminPermissions(role?.profileId, initialRole?.profileId, options);
                calls.push(error);
                if (!error) {
                    setInitialRole(role);
                }
            }

            if (isAccountTeamMemberSettingsChanged) {
                calls.push(await saveAccountTeamMemberSettings(options));
            }
            setBusy(false);

            const error = calls.filter(Boolean).join(' ');

            if (error) {
                setErrorMessage(error);
                return error;
            } else {
                setInitialRole(role);
                proceed();
            }
        } finally {
            setBusy(false);
        }
    }, [
        saveAdminPermissions,
        role,
        initialRole,
        isAccountTeamMemberSettingsChanged,
        saveAccountTeamMemberSettings,
        isInitialAdminPermissions,
        isRoleChanged,
        proceed,
        setErrorMessage,
        setInitialRole,
    ]);

    const discardUserRolePermissions = useCallback(() => {
        discardAdminRole();
        discardAdminPermissions();
        discardAccountTeamMemberSettings();
    }, [discardAdminPermissions, discardAdminRole, discardAccountTeamMemberSettings]);

    const isDefaultAdminPermissions = useMemo(
        () => isDeepEqual({prev: defaultAdminPermissions, current: adminPermissions}),
        [defaultAdminPermissions, adminPermissions],
    );
    const isLoading = [
        isRolesPermissionsLoading,
        isUserPermissionsLoading,
        isUserRoleLoading,
        isAccountTeamMemberSettingsLoading,
    ].some(Boolean);

    const loadingError = [
        rolesPermissionsLoadingError,
        permissionsLoadingError,
        userRoleLoadingError,
        accountTeamMemberSettingsLoadingError,
    ].find(Boolean) || null;

    return children({
        busy,
        isLoading,
        loadingError,
        isChunkChanged: isCurrentChunkChanged,

        roles,
        role, changeRole,
        defaultAdminPermissions,
        adminPermissions, setAdminPermissions,
        accountTeamMemberSettings, setAccountTeamMemberSettings,

        isDefaultAdminPermissions,
        resetAdminPermissions,
        saveChanges: saveUserRolePermissions,
        discardChanges: discardUserRolePermissions,
    });
};
